import {useRef} from 'react';
import {isProductReportSource} from './sourceCitePolicy';

/**
 * Custom hook for handling drag and drop operations on segment items
 */
export function useDragAndDrop({
                                   segmentId,
                                   docId,
                                   docUrl,
                                   docTitle,
                                   docSource,
                                   docSourceType,
                                   classificationLevel,
                                   content,
                                   tags,
                                   index,
                                   offset,
                                   start,
                                   end,
                                   isReference,
                                   orderIndex,
                                   refNumber,
                                   // When this item already lives in the source package, pass its
                                   // Y.Map item id so that drop targets can move instead of re-add.
                                   itemId,
                                   // Stable localId for non-corpus hits (My Data / Reports) where
                                   // docId / index are null. snippetDropPlugin uses this directly.
                                   localId,
                                   // File / report IDs for non-corpus sources — forwarded into the
                                   // Yjs reference entry so the margin-rail viewer can open the
                                   // correct document without needing extractIdFromLocalId fallback.
                                   fileId,
                                   reportId,
                                   productType,
                               }) {
    const dragGhostRef = useRef(null);

    const createDragGhost = (text) => {
        try {
            const ghost = document.createElement('div');
            ghost.style.position = 'fixed';
            ghost.style.top = '-1000px';
            ghost.style.left = '-1000px';
            ghost.style.padding = '6px 10px';
            ghost.style.font = '500 12px system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif';
            ghost.style.color = '#0d47a1';
            ghost.style.background = 'white';
            ghost.style.border = '1px solid #cde';
            ghost.style.borderRadius = '8px';
            ghost.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
            ghost.style.maxWidth = '240px';
            ghost.style.minWidth = '120px';
            ghost.style.whiteSpace = 'nowrap';
            ghost.style.textOverflow = 'ellipsis';
            ghost.style.overflow = 'hidden';
            ghost.style.pointerEvents = 'none';
            ghost.textContent = text || 'Item';
            document.body.appendChild(ghost);
            return ghost;
        } catch (e) {
            return null;
        }
    };

    const handleDragStart = (e) => {
        if (isProductReportSource({docSourceType, reportId, localId, productType})) {
            e.preventDefault();
            return;
        }

        // Validate content before allowing drag
        if (!content || !content.trim()) {
            e.preventDefault();
            console.warn('Cannot drag segment with empty content');
            return;
        }

        // For non-corpus hits (My Data / Reports) segmentId and docId are null.
        // Use localId as the drag item id so the drop target can always dedupe.
        const dragId = segmentId ?? localId ?? null;
        const snippet = {
            id: dragId,
            localId: localId ?? null,
            docId,
            docUrl,
            docTitle,
            docSource,
            docSourceType,
            classificationLevel,
            content,
            tags,
            segmentId,
            // File / report identifiers for non-corpus sources (null for corpus hits).
            fileId: fileId ?? null,
            reportId: reportId ?? null,
            productType: productType ?? null,
            index,
            offset,
            start,
            end,
            // Included so drop targets can call moveItem instead of addItem
            ...(itemId != null ? {itemId} : {}),
        };
        try {
            e.dataTransfer.setData('application/x-snippet', JSON.stringify(snippet));
            // When dragging a reference inside the references list, also include the current order index
            if (isReference && orderIndex !== undefined) {
                e.dataTransfer.setData('application/x-ref-order-index', String(orderIndex));
            }
            e.dataTransfer.effectAllowed = 'copyMove';

            // Use a small custom drag image so the drop target remains visible
            const title = docTitle || (isReference ? 'Reference' : 'Segment');
            const label = isReference && typeof refNumber === 'number' && Number.isFinite(refNumber)
                ? `[${refNumber}] ${title}`
                : title;
            const ghost = createDragGhost(label);
            if (ghost && e.dataTransfer.setDragImage) {
                e.dataTransfer.setDragImage(ghost, 12, -24);
                dragGhostRef.current = ghost;
            }
        } catch (err) {
            console.error('Failed to start drag:', err);
        }
    };

    const handleDragEnd = () => {
        const ghost = dragGhostRef.current;
        if (ghost) {
            try {
                if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
            } catch (e) {
                // ignore
            } finally {
                dragGhostRef.current = null;
            }
        }
    };

    return {
        handleDragStart,
        handleDragEnd
    };
}

